import { textTypes } from 'Shared/backend/widgets/TextWidget';
import { sectionProductTypes } from 'Shared/backend/menu/MenuDesignerPage';
import { ClassContext, embeddedElements, settingsParts } from 'Shared/frontend/styles/ruleProperties';


/* The bare HTML elements a rule can match on. Component.tsx hands 'button' down the style path from a
   buttons widget and 'a' from a link widget, the same way it hands a text component's type down. A
   rule on one of these lands on that widget's root element; a non-inheriting property (border,
   background) will only reach the button once child+class rule locations exist. */
export const elementTypes = {
	button: 'Button',
	a: 'Link',
};


export type LocationClass = {
	name:  string;
	label: string;
};

export type LocationClassGroup = {
	name:    string;
	label:   string;
	classes: LocationClass[];
};


/*
	The classes a Style Builder rule can match on, grouped by where they come from - see
	locationMatches.ts for what a class means in a location expression, and the 'classes' entries in
	StylesProvider's PathElement for what actually puts them on the style path.

	Five things put a class there: Component.tsx hands a text component's type down as one,
	MenuPage's DisplaySectionOrWidget hands down the section's product type (and 'menu-section' on the
	section box), the menu page tags its own leaf elements - the section heading, an item name, a
	section-jump button - with a 'menu-*' class of their own, the event page tags a lineup artist and
	its genres/location line (see Artist.tsx / ArtistImageAndSummary.tsx), and the 'Classes' style
	(toolStyles.ts) applies whichever of the venue's own classes have been ticked.

	A class name may contain spaces and punctuation - the grammar's delimiters are backslash-escaped
	where the name sits in a location expression (see escapeStyleClass in locationMatches.ts).

	The embedded-html elements are the exception to all of the above: they are on no style path at
	all, and are resolved as a nested css selector instead (see embeddedElements in
	ruleProperties.ts). Only the Style Builder can use one, so only that context is offered them.
*/
export function locationClassGroups(venueClasses:string[], context:ClassContext): LocationClassGroup[]
{
	/* The settings page's rule lists have no classes at all: what they offer is the parts of one
	   piece of chrome, which are not on any style path - LocationBuilder lists those itself, off
	   settingsParts. */
	if (settingsParts[context] != undefined)
		return [];

	return [
		{
			name:    'text',
			label:   'Text',
			classes: namedClasses(textTypes),
		},
		{
			name:    'element',
			label:   'Elements',
			classes: namedClasses(elementTypes),
		},
		{
			name:    'embedded',
			label:   'Embedded html',
			classes: context=='styleBuilder' ? embeddedElements.map(e => ({name:e.element, label:e.label})) : [],
		},
		{
			name:    'menu',
			label:   'Menu section type',
			classes: namedClasses(sectionProductTypes),
		},
		{
			name:    'menuPart',
			label:   'Menu',
			classes: [
				{name: 'menu-section-heading', label: 'Section heading'},
				{name: 'menu-item-name',       label: 'Item name'},
				{name: 'menu-section-jump',    label: 'Section jump button'},
				{name: 'menu-section',         label: 'Section box'},
			],
		},
		{
			name:    'eventPart',
			label:   'Event page',
			classes: [
				{name: 'artist', label: 'Lineup artist'},
				{name: 'genres', label: 'Artist genres & location'},
			],
		},
		{
			name:    'venue',
			label:   'Venue classes',
			classes: venueClasses.map(name => ({name, label:name})),
		},
	];
}


/* The label a chosen class is shown by - its group's label alongside its own, since 'Title' on its
   own says nothing about it being a text component. A settings context names a part rather than a
   class, and there is only one list to look in. An unknown name is one the venue has since dropped
   from its list, a text type that has gone, or a part withdrawn from a node: it keeps its name and
   says so. */
export function locationClassLabel(venueClasses:string[], context:ClassContext, aClass:string): string
{
	const parts = settingsParts[context];
	if (parts != undefined)
		return parts.find(entry => entry.part==aClass)?.label ?? `${aClass} (unknown part)`;

	for (const group of locationClassGroups(venueClasses,'styleBuilder'))
		for (const entry of group.classes)
			if (entry.name == aClass)
				return `${group.label}: ${entry.label}`;

	return `${aClass} (unknown class)`;
}


function namedClasses(options:{[name: string]: string}): LocationClass[]
{
	return Object.entries(options).map(([name,label]) => ({name, label}));
}
